import React from "react";
import { prefersReducedMotion } from "./particleField";
import {
  LOADER_COLORS,
  DEFAULT_VARIANT,
  DEFAULT_SIZE,
  loaderPx,
  resolveLoaderContext,
} from "./loaderTokens";

/**
 * Loader — the app's loading indicator, in four flavours.
 *
 * Replaces MUI's CircularProgress, which is generic and reads as "a framework
 * is busy" rather than "your cards are coming". Variants are deliberately
 * unequal in character: `orbit` is the quiet everyday one, the others are
 * reserved for moments rare enough to earn a bit of theatre.
 *
 * Callers should pass `context` ("inline" | "panel" | "grid" | "discovery" |
 * "reveal") rather than variant+size, so the routing stays in one place.
 *
 * Accessibility: announces itself once via role="status". Under
 * prefers-reduced-motion it renders a still, legible mark instead of a faster
 * animation — the setting asks for less motion, not quicker motion.
 */
const Loader = ({
  context,
  variant,
  size,
  label = "Loading",
  inheritColor = false,
  className,
  style,
}) => {
  const resolved = context ? resolveLoaderContext(context) : {};
  const v = variant || resolved.variant || DEFAULT_VARIANT;
  const s = size || resolved.size || DEFAULT_SIZE;
  const px = loaderPx(s);
  const reduced = prefersReducedMotion();

  const C = LOADER_COLORS;
  const tint = inheritColor ? "currentColor" : C.purpleSoft;

  const shell = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: px,
    height: px,
    flexShrink: 0,
    ...style,
  };

  const srOnly = {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  };

  // Reduced motion: one still mark. Keeps the slot filled and the status
  // announced without any movement at all.
  const body = reduced ? (
    <span
      data-testid="loader-static"
      style={{
        width: px * 0.55,
        height: px * 0.55,
        borderRadius: "50%",
        border: `2px solid ${inheritColor ? "currentColor" : C.purpleSoft}`,
        borderTopColor: "transparent",
        opacity: 0.85,
      }}
    />
  ) : (
    renderVariant(v, px, C, tint, inheritColor)
  );

  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="loader"
      data-variant={reduced ? "static" : v}
      data-size={typeof s === "number" ? "custom" : s}
      className={className}
      style={shell}
    >
      {body}
      <span style={srOnly}>{label}</span>
      <LoaderKeyframes />
    </span>
  );
};

const renderVariant = (v, px, C, tint, inheritColor) => {
  switch (v) {
    /* ── Card flip — a card turning over. Blocking, branded waits. ────────── */
    case "flip": {
      const w = px * 0.62;
      const h = px * 0.86;
      const face = {
        position: "absolute",
        inset: 0,
        borderRadius: Math.max(3, px * 0.1),
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        animation: "cldrFlip 1.1s cubic-bezier(0.4, 0.1, 0.3, 1) infinite",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
      return (
        <span style={{ position: "relative", width: w, height: h, perspective: px * 4 }}>
          <span
            style={{
              ...face,
              background: `linear-gradient(145deg, ${C.purple}, ${C.purpleDeep})`,
            }}
          >
            {px >= 40 && (
              <span
                style={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: px * 0.3,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1,
                }}
              >
                C
              </span>
            )}
          </span>
          <span
            style={{
              ...face,
              transform: "rotateY(180deg)",
              background: `linear-gradient(145deg, ${C.foil[0]}, ${C.foil[1]} 45%, ${C.foil[2]} 85%)`,
            }}
          />
        </span>
      );
    }

    /* ── Pulse rings — discovery. Same language as map opportunity pins. ─── */
    case "pulse": {
      const ring = {
        position: "absolute",
        inset: 0,
        margin: "auto",
        width: px * 0.66,
        height: px * 0.66,
        borderRadius: "50%",
        border: `2px solid ${tint}`,
      };
      return (
        <span style={{ position: "relative", width: px, height: px }}>
          <span style={{ ...ring, animation: "cldrRing 1.8s ease-out infinite" }} />
          <span style={{ ...ring, animation: "cldrRing 1.8s ease-out 0.6s infinite" }} />
          <span
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              width: px * 0.24,
              height: px * 0.24,
              borderRadius: "50%",
              background: inheritColor ? "currentColor" : C.purplePale,
            }}
          />
        </span>
      );
    }

    /* ── Dealing hand — a wall of cards arriving. ─────────────────────────── */
    case "deal": {
      const cw = px * 0.3;
      const ch = px * 0.44;
      // Rotation lives on the wrapper: the keyframes animate `transform`, so a
      // rotation set on the same element would be overwritten mid-animation.
      const card = (i, rot, left, delay, light) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left,
            bottom: 0,
            width: cw,
            height: ch,
            transform: `rotate(${rot}deg)`,
            transformOrigin: "bottom center",
          }}
        >
          <span
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              borderRadius: Math.max(2, px * 0.06),
              background: inheritColor
                ? "currentColor"
                : `linear-gradient(160deg, ${light ? C.purpleSoft : C.purple}, ${C.purpleDeep})`,
              animation: `cldrDeal 1.2s ease-in-out ${delay}s infinite`,
            }}
          />
        </span>
      );
      return (
        <span style={{ position: "relative", width: px, height: px * 0.72 }}>
          {card(0, -12, 0, 0, false)}
          {card(1, 0, px * 0.34, 0.2, true)}
          {card(2, 12, px * 0.68, 0.4, false)}
        </span>
      );
    }

    /* ── Orbit dot — the quiet everyday default. ──────────────────────────── */
    case "orbit":
    default: {
      const d = Math.max(4, px * 0.2);
      const dot = (bg, pos, delay) => (
        <span
          style={{
            position: "absolute",
            width: d,
            height: d,
            borderRadius: "50%",
            background: inheritColor ? "currentColor" : bg,
            animation: `cldrFade 1.4s ease-in-out ${delay}s infinite`,
            ...pos,
          }}
        />
      );
      return (
        <span
          style={{
            position: "relative",
            width: px * 0.86,
            height: px * 0.86,
            animation: "cldrOrbit 1.4s linear infinite",
          }}
        >
          {dot(C.foil[0], { top: 0, left: "50%", marginLeft: -d / 2 }, 0)}
          {dot(C.foil[1], { bottom: d * 0.3, left: 0 }, 0.35)}
          {dot(C.foil[2], { bottom: d * 0.3, right: 0 }, 0.7)}
        </span>
      );
    }
  }
};

/** Keyframes are prefixed `cldr` so they cannot collide with page animations. */
const LoaderKeyframes = () => (
  <style>{`
    @keyframes cldrFlip {
      from { transform: rotateY(0deg); }
      to   { transform: rotateY(360deg); }
    }
    @keyframes cldrRing {
      0%   { transform: scale(0.4); opacity: 0.7; }
      100% { transform: scale(1.6); opacity: 0; }
    }
    @keyframes cldrOrbit {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes cldrFade {
      0%, 100% { opacity: 0.25; }
      50%      { opacity: 1; }
    }
    @keyframes cldrDeal {
      0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
      50%      { transform: translateY(-14%) scale(1.05); filter: brightness(1.28); }
    }
  `}</style>
);

export default Loader;
